import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // scrollTo(0,0) is a no-op if already at top → no compositor flush on iOS WKWebView.
    // Micro-scroll to 1px then back forces a real scroll delta → compositor flushes the frame.
    window.scrollTo(0, 1);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [pathname]);
  return null;
}

function IOSRepaintFix() {
  useEffect(() => {
    // Force WKWebView compositor flush on pageshow (fresh load + bfcache restore)
    const flush = () => {
      window.scrollTo(0, 1);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    };
    window.addEventListener('pageshow', flush);
    return () => window.removeEventListener('pageshow', flush);
  }, []);
  return null;
}
import { PasskeyAuthProvider } from "@/contexts/PasskeyAuthContext";
import { NavBarProvider } from "@/contexts/NavBarContext";
import { useVersionDrift } from "@/hooks/useVersionDrift";

function VersionDriftWatcher() {
  useVersionDrift();
  return null;
}

import { DevEnvBanner, DevEnvPadding } from "./components/DevEnvBanner";

// Lazy-loaded pages: only fetched when the user navigates there
const Account = lazy(() => import("./pages/Account"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const MerchantDashboard = lazy(() => import("./pages/MerchantDashboard"));
const MerchantLogin = lazy(() => import("./pages/MerchantLogin"));
const InstanceLogin = lazy(() => import("./pages/InstanceLogin"));
const InstanceSetup = lazy(() => import("./pages/InstanceSetup"));
const InstanceDashboard = lazy(() => import("./pages/InstanceDashboard"));
const ClaimCheck = lazy(() => import("./pages/ClaimCheck"));
const PrintEditor = lazy(() => import("./pages/PrintEditor"));
const WritePage = lazy(() => import("./pages/IssuePage"));
const RedirectProxy = lazy(() => import("./components/RedirectProxy"));
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// No spinner: during lazy-chunk load we simply show the theme background.
// The real content (cheque, forms, etc.) fades in once it's ready — a single
// unified appearance, no multiple competing loaders.
function PageSpinner() {
  return null;
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <PasskeyAuthProvider>
          <NavBarProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <DevEnvBanner />
            <DevEnvPadding />
            <ScrollToTop />
            <IOSRepaintFix />
            <VersionDriftWatcher />
            <Suspense fallback={<PageSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/go/:chequeId" element={<RedirectProxy />} />
                <Route path="/claim/:chequeId" element={<ClaimCheck />} />
                <Route path="/account" element={<Account />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/setup" element={<AdminSetup />} />
                <Route path="/merchant" element={<MerchantDashboard />} />
                <Route path="/merchant/login" element={<MerchantLogin />} />
                <Route path="/instance/login" element={<InstanceLogin />} />
                <Route path="/instance/dashboard" element={<InstanceDashboard />} />
                <Route path="/instance-setup" element={<InstanceSetup />} />
                <Route path="/admin/print/:batchId" element={<PrintEditor />} />
                <Route path="/write" element={<WritePage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </NavBarProvider>
        </PasskeyAuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
